'use client';

import { useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { mediaCache } from '@/lib/media-cache';
import { graphqlClient } from '@/lib/graphql/client';
import { PageMediaResponse } from '@/lib/graphql/schema';
import { waitForPageMedia } from '@/lib/wait-for-page-media';
import { heroVideoState } from '@/lib/hero-video-state';

interface GlobalMediaPreloaderProps {
  currentPageSlug?: string; // Optional - will auto-detect from pathname if not provided
  preloadAllPages?: boolean;
}

function waitForIdleWindow(): Promise<void> {
  return new Promise((resolve) => {
    const win = window as Window & { requestIdleCallback?: typeof requestIdleCallback };
    if (win.requestIdleCallback) {
      win.requestIdleCallback(() => resolve(), { timeout: 3000 });
      return;
    }

    if (document.readyState === 'complete') {
      setTimeout(resolve, 100);
      return;
    }

    globalThis.addEventListener('load', () => setTimeout(resolve, 100), { once: true });
  });
}

function getCurrentPageSlug(pathname: string | null, currentPageSlug?: string): string | null {
  if (currentPageSlug !== undefined) {
    return currentPageSlug;
  }

  if (!pathname || pathname.startsWith('/admin') || pathname.startsWith('/api')) {
    return null;
  }

  if (pathname === '/') {
    return '';
  }

  const slug = pathname.replace(/^\//, '').split('?')[0];
  const fileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.ico', '.mp4', '.webm', '.mov', '.pdf', '.css', '.js', '.json'];
  if (fileExtensions.some(ext => slug.toLowerCase().endsWith(ext))) {
    return null;
  }

  const excludedRoutes = ['en', 'es', 'fr', 'de', 'it', 'pt', 'api', '_next', 'favicon.ico'];
  if (excludedRoutes.includes(slug.toLowerCase())) {
    return null;
  }

  return slug;
}

function getPreloadUrls(pageMedia: PageMediaResponse): { criticalUrls: string[]; deferredUrls: string[] } {
  const criticalUrls = pageMedia.criticalMedia
    .map(media => media.playbackUrl || media.url)
    .filter(Boolean);
  const deferredUrls = pageMedia.highPriorityMedia
    .map(media => media.playbackUrl || media.url)
    .filter(Boolean);

  return {
    criticalUrls: Array.from(new Set(criticalUrls)),
    deferredUrls: Array.from(new Set(deferredUrls)),
  };
}

/**
 * Public-page media preloader.
 * Uses the public GraphQL endpoint and only preloads current-page media.
 */
export default function GlobalMediaPreloader({
  currentPageSlug,
  preloadAllPages: _preloadAllPages = false,
}: GlobalMediaPreloaderProps) {
  if (typeof window === 'undefined') {
    return null;
  }

  const pathname = usePathname();
  const currentSlug = useMemo(
    () => getCurrentPageSlug(pathname, currentPageSlug),
    [pathname, currentPageSlug],
  );

  useEffect(() => {
    heroVideoState.reset();
  }, [currentSlug]);

  useEffect(() => {
    if (currentSlug === null) {
      return;
    }

    let isCancelled = false;

    const preloadCurrentPage = async () => {
      try {
        await waitForIdleWindow();
        if (isCancelled) {
          return;
        }

        const response = await graphqlClient.queryGet<{ pageMediaOptimized: PageMediaResponse }>(
          'pageMediaOptimized',
          { pageSlug: currentSlug || '/' },
        );
        const pageMedia = response?.pageMediaOptimized;

        if (isCancelled || !pageMedia || pageMedia.totalMediaCount === 0) {
          return;
        }

        const { criticalUrls, deferredUrls } = getPreloadUrls(pageMedia);
        if (criticalUrls.length > 0) {
          await mediaCache.preloadBatch(criticalUrls, 'high');
        }

        if (!isCancelled && deferredUrls.length > 0) {
          mediaCache.addToQueue(deferredUrls);
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              mediaCache.processQueue('low').catch(() => undefined);
            }, { timeout: 8000 });
          } else {
            setTimeout(() => {
              mediaCache.processQueue('low').catch(() => undefined);
            }, 1000);
          }
        }

        await waitForPageMedia(8000);
      } catch {
        return;
      }
    };

    preloadCurrentPage();

    return () => {
      isCancelled = true;
    };
  }, [currentSlug]);

  return null;
}
